import { inject, Injectable } from '@angular/core';
import { AgentCategory, AgentHost } from '../models/agent-config.model';
import { AiConversation, AiConversationSummary, AiRequestOptions, AiTransport } from '../transports/ai-transport';
import { FoundryTransport } from '../transports/foundry.transport';
import { AgentRegistryService } from './agent-registry.service';

/**
 * Public API for invoking AI agents. Feature code calls
 * `startConversation(category)` and stays unaware of which host or transport
 * answers.
 */
@Injectable({ providedIn: 'root' })
export class AiClientService {
  readonly #foundryTransport: FoundryTransport = inject(FoundryTransport);
  readonly #registry: AgentRegistryService = inject(AgentRegistryService);

  /** Opens a new conversation with the category's agent; `options` limits the create request. */
  async startConversation(category: AgentCategory, metadata?: Record<string, string>, options?: AiRequestOptions): Promise<AiConversation> {
    const agent = this.#requireAgent(category);
    return this.#pickTransport(agent.host).startConversation(agent, metadata, options);
  }

  async resumeConversation(category: AgentCategory, conversationId: string): Promise<AiConversation> {
    const agent = this.#requireAgent(category);
    return this.#pickTransport(agent.host).resumeConversation(agent, conversationId);
  }

  async deleteConversation(category: AgentCategory, conversationId: string): Promise<void> {
    const agent = this.#requireAgent(category);
    return this.#pickTransport(agent.host).deleteConversation(agent, conversationId);
  }

  /** The signed-in user's conversations with the category's agent, most recently created first, deleted ones
   *  included, read until `enough` holds for the conversations read so far or the list ends. */
  async listConversations(
    category: AgentCategory,
    enough?: (read: AiConversationSummary[]) => boolean,
  ): Promise<AiConversationSummary[]> {
    const agent = this.#requireAgent(category);
    return this.#pickTransport(agent.host).listConversations(agent, enough);
  }


  #requireAgent(category: AgentCategory) {
    const agent = this.#registry.getAgent(category);
    if (!agent) {
      throw new Error(`No agent registered for category ${AgentCategory[category]}`);
    }
    return agent;
  }

  /** Resolves true once the category has an agent on the device, or false when none arrives within `waitMs`. */
  async whenAvailable(category: AgentCategory, waitMs: number): Promise<boolean> {
    return (await this.#registry.whenAvailable(category, waitMs)) !== null;
  }

  isAvailable(category: AgentCategory): boolean {
    return this.#registry.isAvailable(category);
  }

  #pickTransport(host: AgentHost): AiTransport {
    switch (host) {
      case AgentHost.Foundry:
        return this.#foundryTransport;
      default:
        throw new Error(`Unsupported agent host: ${AgentHost[host]}`);
    }
  }
}
